/**
 * Sistem Ayarları › SMS Kayıtları (backlog #637, backend #636): gönderilen / başarısız olan / bilerek tutulan her SMS'in kaydı.
 * Süzgeçler: durum, kategori, şablon anahtarı, tarih aralığı, numara / metin araması. Tablo sayfalıdır. Satıra tıklayınca ayrıntı penceresi açılır.
 *
 * Gizlilik: kayıtta tek kullanımlık kod ya da geçici şifre ASLA bulunmaz (backend maskeler); numara yetkisiz kişide maskelidir (backend karar verir, sayfa gelen metni
 * olduğu gibi gösterir). Hata sütunu `KOD: ayrıntı` biçimindedir; bilinen kodlar (CREDENTIALS, BALANCE, NO_ANSWER ...) kullanıcının dilindeki açıklamayla gösterilir,
 * ham metin ayrıntı olarak altında kalır.
 */
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Spinner, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import SmsSayfa from './SmsSayfa';
import { getLog, listLogs, sayfala } from '../../../../services/smsAyarService';
import { currentLocaleTag } from '../../../../i18n';

const SAYFA_BOYUTU = 20;
const DURUM_RENK = { SENT: 'success', FAILED: 'danger', BLOCKED: 'warning' };
const ILETIM_RENK = { DELIVERED: 'success', FAILED: 'danger', WAITING: 'secondary', PENDING: 'secondary', UNKNOWN: 'secondary' };
const BOS_SUZGEC = { status: '', category: '', templateKey: '', from: '', to: '', q: '' };

const tarih = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString(currentLocaleTag()) : '';
};

/** `CREDENTIALS: ham metin` → { kod: 'CREDENTIALS', ayrinti: 'ham metin' }; kodsuz metin yalnız ayrıntıdır. */
export function hataAyir(hata) {
  const m = /^([A-Z_]+):\s*(.*)$/s.exec(hata || '');
  return m ? { kod: m[1], ayrinti: m[2] } : { kod: null, ayrinti: hata || '' };
}

const SmsKayitlariPage = () => {
  const { t } = useTranslation('sms');
  const [suzgec, setSuzgec] = useState(BOS_SUZGEC);
  const [uygulanan, setUygulanan] = useState(BOS_SUZGEC);
  const [sayfa, setSayfa] = useState(0);
  const [veri, setVeri] = useState({ satirlar: [], toplam: 0, sayfaSayisi: 1 });
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [ayrinti, setAyrinti] = useState(null);   // { id, satir, yukleniyor, hata }

  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const v = sayfala(await listLogs({ page: sayfa, size: SAYFA_BOYUTU, ...uygulanan }), SAYFA_BOYUTU);
        if (!iptal) { setVeri(v); setHata(null); }
      } catch (e) {
        if (!iptal) setHata(e.message);
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
  }, [sayfa, uygulanan]);

  const alan = (k) => (e) => setSuzgec((s) => ({ ...s, [k]: e.target.value }));
  const ara = (e) => { e.preventDefault(); setYukleniyor(true); setSayfa(0); setUygulanan(suzgec); };
  const temizle = () => { setSuzgec(BOS_SUZGEC); setYukleniyor(true); setSayfa(0); setUygulanan(BOS_SUZGEC); };

  async function ayrintiAc(satir) {
    setAyrinti({ id: satir.sms_log_id, satir, yukleniyor: true });
    try {
      setAyrinti({ id: satir.sms_log_id, satir: await getLog(satir.sms_log_id) });
    } catch (e) {
      setAyrinti({ id: satir.sms_log_id, satir, hata: e.message });
    }
  }

  const hataMetni = (h) => {
    const { kod, ayrinti: a } = hataAyir(h);
    return kod ? { baslik: t(`kayitlar.hatalar.${kod}`, { defaultValue: kod }), ayrinti: a } : { baslik: null, ayrinti: a };
  };

  return (
    <SmsSayfa aktif="kayitlar">
      {hata && <Alert color="danger" fade={false} data-testid="sms-kayit-hata">{hata}</Alert>}
      <Card>
        <CardBody>
          <form onSubmit={ara} data-testid="sms-kayit-suzgec">
            <Row className="g-2 align-items-end mb-3">
              <Col md={2}>
                <Label htmlFor="sms-f-durum" className="small mb-0">{t('kayitlar.durum')}</Label>
                <Input id="sms-f-durum" type="select" value={suzgec.status} onChange={alan('status')}>
                  <option value="">{t('kayitlar.hepsi')}</option>
                  {['SENT', 'FAILED', 'BLOCKED'].map((d) => <option key={d} value={d}>{t(`durum.${d}`)}</option>)}
                </Input>
              </Col>
              <Col md={2}>
                <Label htmlFor="sms-f-kategori" className="small mb-0">{t('kayitlar.kategori')}</Label>
                <Input id="sms-f-kategori" type="select" value={suzgec.category} onChange={alan('category')}>
                  <option value="">{t('kayitlar.hepsi')}</option>
                  <option value="OTP">OTP</option>
                  <option value="HIZMET">HIZMET</option>
                </Input>
              </Col>
              <Col md={2}>
                <Label htmlFor="sms-f-sablon" className="small mb-0">{t('kayitlar.sablon')}</Label>
                <Input id="sms-f-sablon" value={suzgec.templateKey} onChange={alan('templateKey')} maxLength={64} />
              </Col>
              <Col md={2}>
                <Label htmlFor="sms-f-baslangic" className="small mb-0">{t('kayitlar.baslangic')}</Label>
                <Input id="sms-f-baslangic" type="date" value={suzgec.from} onChange={alan('from')} />
              </Col>
              <Col md={2}>
                <Label htmlFor="sms-f-bitis" className="small mb-0">{t('kayitlar.bitis')}</Label>
                <Input id="sms-f-bitis" type="date" value={suzgec.to} onChange={alan('to')} />
              </Col>
              <Col md={2}>
                <Label htmlFor="sms-f-ara" className="small mb-0">{t('kayitlar.ara')}</Label>
                <Input id="sms-f-ara" value={suzgec.q} onChange={alan('q')} placeholder={t('kayitlar.araIpucu')} />
              </Col>
              <Col xs={12} className="d-flex gap-2">
                <Button color="primary" type="submit" id="sms-f-uygula">{t('kayitlar.uygula')}</Button>
                <Button color="light" type="button" id="sms-f-temizle" onClick={temizle}>{t('kayitlar.temizle')}</Button>
              </Col>
            </Row>
          </form>

          {yukleniyor ? <div className="text-center py-4"><Spinner /></div> : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" data-testid="sms-kayit-tablosu">
                <thead>
                  <tr>
                    <th>{t('kayitlar.zaman')}</th><th>{t('kayitlar.numara')}</th><th>{t('kayitlar.kullanim')}</th><th>{t('kayitlar.sablon')}</th>
                    <th>{t('kayitlar.durum')}</th><th>{t('kayitlar.parca')}</th><th>{t('kayitlar.maliyet')}</th><th />
                  </tr>
                </thead>
                <tbody>
                  {veri.satirlar.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-4">{t('kayitlar.bos')}</td></tr>}
                  {veri.satirlar.map((r) => {
                    const h = hataMetni(r.sms_log_error);
                    return (
                      <tr key={r.sms_log_id} data-testid={`sms-kayit-${r.sms_log_id}`}>
                        <td className="text-nowrap small">{tarih(r.sms_log_created_at)}</td>
                        <td className="text-nowrap">{r.sms_log_to}</td>
                        <td className="small">{r.sms_log_usage}<div className="text-muted">{r.sms_log_category}</div></td>
                        <td className="small">{r.sms_log_template_key || <span className="text-muted">—</span>}</td>
                        <td>
                          <Badge color={DURUM_RENK[r.sms_log_status] || 'secondary'}>{t(`durum.${r.sms_log_status}`, { defaultValue: r.sms_log_status })}</Badge>
                          {r.sms_log_delivery_status && (
                            <Badge color={ILETIM_RENK[r.sms_log_delivery_status] || 'secondary'} className="ms-1">{t(`kayitlar.iletim.${r.sms_log_delivery_status}`, { defaultValue: r.sms_log_delivery_status })}</Badge>
                          )}
                          {h.baslik && <div className="small text-danger mt-1">{h.baslik}</div>}
                        </td>
                        <td>{r.sms_log_parts ?? ''}</td>
                        <td>{r.sms_log_cost ?? ''}</td>
                        <td className="text-end"><Button size="sm" color="light" id={`sms-kayit-ayrinti-${r.sms_log_id}`} onClick={() => ayrintiAc(r)}><i className="bx bx-show" /></Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {veri.sayfaSayisi > 1 && (
            <Pagination className="justify-content-end mt-3 mb-0" data-testid="sms-kayit-sayfalar">
              <PaginationItem disabled={sayfa === 0}><PaginationLink previous id="sms-onceki" onClick={() => { setYukleniyor(true); setSayfa(sayfa - 1); }} /></PaginationItem>
              <PaginationItem active><PaginationLink>{t('kayitlar.sayfa', { sayfa: sayfa + 1, toplam: veri.sayfaSayisi })}</PaginationLink></PaginationItem>
              <PaginationItem disabled={sayfa + 1 >= veri.sayfaSayisi}><PaginationLink next id="sms-sonraki" onClick={() => { setYukleniyor(true); setSayfa(sayfa + 1); }} /></PaginationItem>
            </Pagination>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={!!ayrinti} toggle={() => setAyrinti(null)} size="lg" centered>
        {ayrinti && (
          <>
            <ModalHeader toggle={() => setAyrinti(null)}>{t('kayitlar.ayrintiBasligi', { id: ayrinti.id })}</ModalHeader>
            <ModalBody data-testid="sms-kayit-ayrinti">
              {ayrinti.hata && <Alert color="danger" fade={false}>{ayrinti.hata}</Alert>}
              {ayrinti.yukleniyor ? <div className="text-center py-3"><Spinner /></div> : (() => {
                const r = ayrinti.satir;
                const h = hataMetni(r.sms_log_error);
                const satirlar = [
                  ['zaman', tarih(r.sms_log_created_at)], ['numara', r.sms_log_to], ['kullanim', `${r.sms_log_usage} / ${r.sms_log_category}`], ['sablon', r.sms_log_template_key],
                  ['durum', t(`durum.${r.sms_log_status}`, { defaultValue: r.sms_log_status })], ['iletimDurumu', r.sms_log_delivery_status], ['iletimZamani', tarih(r.sms_log_delivery_at)],
                  ['parca', r.sms_log_parts], ['maliyet', r.sms_log_cost], ['saticiNo', r.sms_log_vendor_id], ['hesap', r.sms_account_id],
                  ['kullanici', r.user_id || null], ['kisi', r.person_id || null],
                ].filter(([, v]) => v !== null && v !== undefined && v !== '');
                return (
                  <>
                    <Table borderless size="sm" className="mb-3">
                      <tbody>{satirlar.map(([k, v]) => <tr key={k}><th className="text-muted fw-normal" style={{ width: 180 }}>{t(`kayitlar.${k}`)}</th><td>{String(v)}</td></tr>)}</tbody>
                    </Table>
                    {r.sms_log_text && (<><div className="small text-muted mb-1">{t('kayitlar.metin')}</div><pre className="border rounded p-2 bg-light mb-3" style={{ whiteSpace: 'pre-wrap' }}>{r.sms_log_text}</pre></>)}
                    {r.sms_log_error && (
                      <Alert color="danger" fade={false} className="mb-0">
                        {h.baslik && <div className="fw-semibold">{h.baslik}</div>}
                        {h.ayrinti && <div className="small">{h.ayrinti}</div>}
                      </Alert>
                    )}
                    <div className="small text-muted mt-3">{t('kayitlar.gizlilikNotu')}</div>
                  </>
                );
              })()}
            </ModalBody>
            <ModalFooter><Button color="light" onClick={() => setAyrinti(null)}>{t('kapat')}</Button></ModalFooter>
          </>
        )}
      </Modal>
    </SmsSayfa>
  );
};

export default SmsKayitlariPage;
